import CountryListModule, {
  Country,
} from 'country-list-with-dial-code-and-flag';
import countries from 'i18n-iso-countries';
import deLocale from 'i18n-iso-countries/langs/de.json';
import React, { useEffect, useState } from 'react';
import { FlagIcon } from 'react-flag-kit';
import InputField from '../../../common/input-field/input-field';
import { ReactComponent as ChevronDown } from '../icons/chevron-down.svg';
import './international-phone-input.css';

// Handle CJS/ESM interop – the actual class may be on `.default`
const CountryList = ((CountryListModule as any).default ??
  CountryListModule) as typeof CountryListModule;

interface InternationalPhoneInputProps extends React.HTMLAttributes<any> {
  label: string;
  name: string;
  getCode: (value: string) => void;
  code?: string;
  placeholder?: string;
  error: string | undefined;
}

const InternationalPhoneInput = React.forwardRef<
  any,
  InternationalPhoneInputProps
>(
  (
    {
      label,
      name,
      error,
      placeholder,
      getCode,
      code,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    // Select countries local
    countries.registerLocale(deLocale);

    // Code State
    const [countryCode, setCountryCode] = useState<string>(code || '+49');

    // Flag State
    const [flag, setFlag] = useState<string>('DE');

    // Sync state when code prop changes (adjust state during render)
    // React 19 pattern: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    const [prevCode, setPrevCode] = useState(code);
    if (code !== prevCode) {
      setPrevCode(code);
      if (code) {
        setCountryCode(code);
        const selectedCountry = CountryList.findByDialCode(code)[0];
        setFlag(selectedCountry.code);
      }
    }

    // Active State
    const [active, setActive] = useState<boolean>(false);

    // Focused item index for keyboard navigation
    const [focusedIndex, setFocusedIndex] = useState<number>(-1); // -1 means no item is focused

    // On click in flag area
    const handleOnClick = () => {
      setActive(!active);
      if (!active) setFocusedIndex(-1);
    };

    // Keyboard handler with arrow navigation
    const handleKeyDown = (event: React.KeyboardEvent) => {
      const countries = getCountriesList();

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!active) {
            setActive(true);
            setFocusedIndex(0);
          } else if (focusedIndex >= 0) {
            const selectedCountry = countries[focusedIndex];
            handleOnChange(selectedCountry.dial_code);
            setFlag(selectedCountry.code);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setActive(false);
          setFocusedIndex(-1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!active) {
            setActive(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) => Math.min(prev + 1, countries.length - 1));
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (active && focusedIndex > 0) {
            setFocusedIndex((prev) => prev - 1);
          }
          break;
      }
    };

    // Handle clicking outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (
          !target.closest('.icon-container') &&
          !target.closest('.intl-countries-list')
        ) {
          setActive(false);
          setFocusedIndex(-1);
        }
      };

      if (active) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [active]);

    // Scroll focused item into view
    useEffect(() => {
      if (focusedIndex >= 0 && active) {
        const listElement = document.querySelector(
          '.intl-countries-list.active',
        );
        const focusedElement = listElement?.children[
          focusedIndex
        ] as HTMLElement;
        focusedElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, [focusedIndex, active]);

    // On change in value
    const handleOnChange = (value: string) => {
      setActive(false);
      setFocusedIndex(-1);
      getCode(value);
      if (value.length > 2 && value[0] === '+') {
        setCountryCode(value);
      }
    };

    // Get list of countries
    const getCountriesList = () => {
      const list = CountryList.getAll()
        .filter((country) => {
          // Filter missing flag countries
          if (country.name === 'Netherlands Antilles') {
            return false;
          }
          if (country.name === 'Antarctica') {
            return false;
          }
          if (country.name === 'Canary Islands') {
            return false;
          }
          if (country.name === 'Caribbean Netherlands') {
            return false;
          }
          if (country.name === 'Western Sarah') {
            return false;
          }

          return country;
        })
        .map((country): Country => {
          return {
            name: countries.getName(country.code, 'de'),
            code: country.code,
            flag: country.flag,
            dial_code: country.dial_code,
          } as Country;
        })
        .sort((countryA, countryB) => {
          // Sort all countries
          if (countryA.name < countryB.name) {
            return -1;
          }

          if (countryA.name > countryB.name) {
            return 1;
          }

          return 0;
        })
        .sort((countryA, countryB) => {
          // Add Germany to top
          if (countryA.name === 'Deutschland') {
            return -1;
          }
          if (countryB.name === 'Deutschland') {
            return 1;
          }

          // Add Austria to top
          if (countryA.name === 'Österreich') {
            return -1;
          }
          if (countryB.name === 'Österreich') {
            return 1;
          }

          // Add Switzerland to top
          if (countryA.name === 'Schweiz') {
            return -1;
          }

          if (countryB.name === 'Schweiz') {
            return 1;
          }

          // Add Ägypten to top
          if (countryA.name === 'Ägypten') {
            return -1;
          }

          if (countryB.name === 'Ägypten') {
            return 1;
          }

          // Add Äquatorialguinea to top
          if (countryA.name === 'Äquatorialguinea') {
            return -1;
          }

          if (countryB.name === 'Äquatorialguinea') {
            return 1;
          }

          // Add Äthiopien to top
          if (countryA.name === 'Äthiopien') {
            return -1;
          }

          if (countryB.name === 'Äthiopien') {
            return 1;
          }

          // Add Åland to top
          if (countryA.name === 'Åland') {
            return -1;
          }

          if (countryB.name === 'Åland') {
            return 1;
          }

          return 0;
        });
      return list;
    };

    return (
      <InputField
        ref={ref}
        onFocus={onFocus}
        onBlur={onBlur}
        name={name}
        icon={
          <>
            <div
              className='icon-container focus-style'
              role='button'
              tabIndex={0}
              aria-expanded={active}
              aria-haspopup='listbox'
              onClick={() => handleOnClick()}
              onKeyDown={handleKeyDown}
            >
              <span className='icon'>
                {' '}
                <FlagIcon
                  code={flag as any}
                  size={13}
                  width='auto'
                  style={{ display: 'inline', width: 'auto' }}
                />
              </span>
              <span className='input-text'>{countryCode}</span>
              <ChevronDown className='input-intl-number-arrow' />
            </div>
            <ul
              className={`intl-countries-list ${active ? 'active' : ''}`}
              role='listbox'
              aria-label='Country selection'
            >
              {getCountriesList().map((country, index) => (
                <li
                  key={index}
                  className={`input-text intl-country ${
                    focusedIndex === index ? 'focused' : ''
                  }`}
                  role='option'
                  tabIndex={-1}
                  aria-selected={country.dial_code === countryCode}
                  onClick={() => {
                    handleOnChange(country.dial_code);
                    setFlag(country.code);
                  }}
                >
                  <span>
                    <FlagIcon
                      code={country.code as any}
                      size={13}
                      width='auto'
                      style={{ display: 'inline', width: 'auto' }}
                    />
                  </span>
                  <span>{country.name}</span>
                  <span>{country.dial_code}</span>
                </li>
              ))}
            </ul>
          </>
        }
        {...props}
        label={label}
        error={error}
        placeholder={placeholder}
      />
    );
  },
);

export default InternationalPhoneInput;
